import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { jwtDecode, PanAppState } from '@panamax/app-state';
import { CreditStatus } from '@payments/constants/payment-dashboard';
import {
  CustomerSellerMetrics,
  PaymentDashboardPageViewModel,
} from '@payments/models/payment-dashboard-page-view.model';
import { PaymentFilter } from '@payments/pages/payment-activity/services/payment-activity.service';
import { getPaymentDashboardPageViewModel } from '@payments/selectors/payments.selector';
import { ModalFilterService } from '@payments/services/payments-modal.service';
import { FEATURES } from '@shared/constants/splitio-features';
import { SplitService } from '@splitsoftware/splitio-angular';
import {
  Filter,
  PaymentStoreService,
  SortDirection,
  SortFilter,
} from '@usf/ngrx-payment';
import { Status } from '@usf/payment-types';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestAll,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';

export interface DashboardFilter {
  filterName: string;
  value: string | number | string[] | number[];
}

export enum DashboardFilterIds {
  closeToTerms = 'closeToTerms',
  creditStatus = 'creditStatus',
  currentPercentageBelow100 = 'currentPercentageBelow100',
  customer = 'customer',
  lastPaymentMethod = 'lastPaymentMethod',
  pastTerms = 'pastTerms',
  terms = 'terms',
}

export enum DashboardSection {
  accountActivity = 'accountActivity',
  arAging = 'arAging',
}

export enum DashboardSortFields {
  customer = 'customerName',
  creditStatus = 'creditStatus',
  seller = 'seller',
  openInvoices = 'openInvoices',
  unusedCredits = 'unusedCredits',
  paymentMethod = 'paymentMethod',
  percentageCurrent = 'currentPercentage',
  accountBalance = 'accountBalance',
  currentBalance = 'currentBalance',
  overdueBalance = 'overDueBalance',
  term = 'term',
  oneSevenDays = 'oneSevenDays',
  eightFifteenDays = 'eightFifteenDays',
  sixteenThirtyDays = 'sixteenThirtyDays',
  thirtyPlus = 'thirtyPlus',
}

@Injectable({
  providedIn: 'root',
})
export class PaymentDashboardService implements ModalFilterService {
  readonly itemsPerPage = 500;
  readonly initialSort: SortFilter[] = [
    {
      fieldName: DashboardSortFields.creditStatus,
      sortValue: SortDirection.ASC,
    },
    {
      fieldName: DashboardSortFields.customer,
      sortValue: SortDirection.ASC,
    },
  ];
  private filtersSubject = new BehaviorSubject<DashboardFilter[]>([]);
  private sortSubject = new BehaviorSubject<SortFilter[]>(this.initialSort);
  private searchSubject = new BehaviorSubject<string>('');

  constructor(
    private store: Store,
    private panAppState: PanAppState,
    private paymentStoreService: PaymentStoreService,
    private splitService: SplitService,
  ) {}

  getInitialData() {
    this.paymentStoreService.getMetricSummary();
    this.paymentStoreService.getMetricDetails();
    this.clearFiltersAndResetSort();
  }

  reloadCustomerMetricsForCustomers(customerNumbers: number[]) {
    let nonEmptyNumbers: number[];
    if (customerNumbers?.length) {
      const uniqueNumbers = new Set(customerNumbers);
      nonEmptyNumbers = Array.from(uniqueNumbers).filter(
        customerNumber => !!customerNumber,
      );
    }

    this.paymentStoreService.getMetricSummary(nonEmptyNumbers);
    this.paymentStoreService.getMetricDetails(nonEmptyNumbers);
  }

  getPaymentDashboardViewModel$(): Observable<PaymentDashboardPageViewModel> {
    return combineLatest([
      this.store.select(getPaymentDashboardPageViewModel),
      this.isNetworkUser(),
      this.userFeature(FEATURES.split_user_payments_dashboard.name),
      this.userFeature(FEATURES.split_user_payments_dashboard_override.name),
      this.userFeature(FEATURES.split_dynamic_payments_dsm_dashboard.name),
      this.filtersSubject,
      this.sortSubject,
      this.searchSubject,
    ]).pipe(
      map(
        ([
          vm,
          isNetworkUser,
          isPaymentsDashboardOn,
          overrideNetworkUser,
          isDsmDashboardOn,
          filters,
          sort,
        ]) => {
          const sortedAndFilteredMetrics = this.filterAndSortMetrics(
            vm.customerMetrics,
            filters,
            sort,
          );
          const isDashboardOn =
            isPaymentsDashboardOn &&
            (!!isNetworkUser || overrideNetworkUser) &&
            !vm.isSuperUser;
          return {
            ...vm,
            customerMetrics: sortedAndFilteredMetrics,
            filters,
            isDashboardOn,
            isDsmDashboardOn: isDsmDashboardOn && vm.dsmInfo.length > 0,
            sort,
          };
        },
      ),
    );
  }

  private isNetworkUser(): Observable<boolean> {
    return this.panAppState.accessToken$.pipe(
      switchMap(token =>
        of(jwtDecode(token)?.['usf-claims']?.userType === 'corp-ad'),
      ),
    );
  }

  private userFeature(featureFlagName: string) {
    const feature$ = this.panAppState.user$.pipe(
      filter(user => !!user?.userId),
      take(1),
      map(user => {
        if (this.splitService.getSDKClient(user.userId) === undefined) {
          this.splitService.initClient(user.userId);
        }
        return this.splitService
          .getClientSDKReady(user.userId)
          .pipe(
            map(() =>
              this.splitService.getTreatment(user.userId, featureFlagName),
            ),
          );
      }),
    );

    return feature$.pipe(combineLatestAll(value => value === 'on'));
  }

  private filterAndSortMetrics(
    customerMetrics: CustomerSellerMetrics[],
    filters: DashboardFilter[],
    sort: SortFilter[],
  ): CustomerSellerMetrics[] {
    let newCustomerMetrics = [...customerMetrics];
    filters.forEach(filter => {
      if (filter.filterName === DashboardFilterIds.creditStatus) {
        newCustomerMetrics = newCustomerMetrics.filter(
          metrics =>
            (filter.value as string[]).indexOf(metrics.creditStatus) >= 0,
        );
      }
      if (filter.filterName === DashboardFilterIds.customer) {
        newCustomerMetrics = newCustomerMetrics.filter(
          metrics =>
            (filter.value as number[]).indexOf(metrics.customerNumber) >= 0,
        );
      }
      if (filter.filterName === DashboardFilterIds.lastPaymentMethod) {
        newCustomerMetrics = newCustomerMetrics.filter(
          metrics =>
            (filter.value as string[]).indexOf(metrics.paymentMethod) >= 0,
        );
      }
      // TileFilters
      if (filter.filterName === DashboardFilterIds.pastTerms) {
        newCustomerMetrics = newCustomerMetrics.filter(
          metrics => metrics.overDueBalance > 0,
        );
      }
      if (filter.filterName === DashboardFilterIds.closeToTerms) {
        newCustomerMetrics = newCustomerMetrics.filter(
          metrics => metrics.closeToTerm,
        );
      }
      if (filter.filterName === DashboardFilterIds.currentPercentageBelow100) {
        if (
          newCustomerMetrics.some(metrics => metrics.currentPercentage < 100)
        ) {
          newCustomerMetrics = newCustomerMetrics.filter(
            metrics => metrics.currentPercentage < 100,
          );
        }
      }
    });

    return this.sortCustomerMetrics(newCustomerMetrics, sort);
  }

  private sortCustomerMetrics(
    customerMetrics: CustomerSellerMetrics[],
    sortFilters: SortFilter[],
  ): CustomerSellerMetrics[] {
    let newCustomerMetrics = [...customerMetrics];
    let mutableSortFilters = [...sortFilters];
    if (newCustomerMetrics.length > 0 && sortFilters.length > 0) {
      mutableSortFilters.reverse().forEach(sortFilter => {
        const fieldName = sortFilter.fieldName;
        if (
          fieldName === DashboardSortFields.customer ||
          fieldName === DashboardSortFields.paymentMethod
        ) {
          newCustomerMetrics.sort((a, b) =>
            a[fieldName]
              .toLowerCase()
              .localeCompare(b[fieldName].toLowerCase()),
          );
        } else if (fieldName === DashboardSortFields.term) {
          newCustomerMetrics.sort((a, b) =>
            this.getTermWithAdjustedSingleDigits(a[fieldName])
              .toLowerCase()
              .localeCompare(
                this.getTermWithAdjustedSingleDigits(
                  b[fieldName],
                ).toLowerCase(),
              ),
          );
        } else if (fieldName === DashboardSortFields.creditStatus) {
          newCustomerMetrics.sort(
            (a, b) =>
              this.getCreditStatusOrderValue(a[fieldName]) -
              this.getCreditStatusOrderValue(b[fieldName]),
          );
        } else if (fieldName === DashboardSortFields.seller) {
          newCustomerMetrics = this.sortMetricsBySellers(newCustomerMetrics);
        } else {
          newCustomerMetrics.sort((a, b) => a[fieldName] - b[fieldName]);
        }

        if (sortFilter.sortValue === SortDirection.DESC) {
          newCustomerMetrics.reverse();
        }
      });
    }
    return newCustomerMetrics;
  }

  /*
   * Sorts sellers by grouping customers by seller and sorting each as an individual list
   * by customer name
   */
  private sortMetricsBySellers(newCustomerMetrics: CustomerSellerMetrics[]) {
    const myCustomersKey = 'my-customers';
    const groupedMetrics: Map<string, CustomerSellerMetrics[]> = new Map([
      [myCustomersKey, []],
    ]);

    newCustomerMetrics.forEach(metrics => {
      const sellerKey = metrics.seller ? metrics.seller : myCustomersKey;
      const group = groupedMetrics.get(sellerKey) ?? [];
      group.push(metrics);
      groupedMetrics.set(sellerKey, group);
    });

    groupedMetrics.forEach(group =>
      group.sort((a, b) => a.customerName.localeCompare(b.customerName)),
    );

    let metricKeys = Array.from(groupedMetrics.keys());
    metricKeys = metricKeys.filter(key => key !== myCustomersKey);
    metricKeys.sort();
    metricKeys = [myCustomersKey, ...metricKeys];

    const sortedMetrics: CustomerSellerMetrics[] = [];

    metricKeys.forEach(sellerKey =>
      sortedMetrics.push(...groupedMetrics.get(sellerKey)),
    );

    return sortedMetrics;
  }

  private getCreditStatusOrderValue(creditStatus: string) {
    return (Object.values(CreditStatus) as string[]).indexOf(creditStatus);
  }

  private getTermWithAdjustedSingleDigits(term: string) {
    return term.replace(/(\s?\b)(\d{1}[\s%]?\b)/g, '$10$2');
  }

  // ModalFilterService

  getFiltersAndSortInformation$(): Observable<{
    filters: Filter[];
    sort: SortFilter[];
  }> {
    return combineLatest([this.filtersSubject, this.sortSubject]).pipe(
      map(([filters, sort]) => {
        return { filters, sort };
      }),
    );
  }

  getStatusOptions(): Map<Status, string> {
    return new Map([]);
  }

  setFilter(
    invoiceFilters: Filter[] | PaymentFilter[] | DashboardFilter[],
  ): void {
    this.filtersSubject.next(invoiceFilters);
  }

  setSort(sortOrder: SortFilter[]) {
    this.sortSubject.next(sortOrder);
  }

  clearFiltersAndResetSort() {
    this.filtersSubject.next([]);
    this.sortSubject.next(this.initialSort);
    this.searchSubject.next('');
  }
}
